// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext } from 'react';
import type {
  CaretPositionGetterReturnValues,
  RuleItemRef,
  UserActionObject,
  SemanticLine,
  RuleItemRefs,
  CaretControlContext,
  IsolatedEditingBlockData,
} from './types';
import type {
  Action,
  BooleanRule,
  ExpressionTerm,
  IndexedRule,
} from '../../../store/engine/types';
import type { RulesWithMLA } from '../mla/types';
import type { RuleUpdateErrorType } from '../../../store/engine/update/constants';
import type { NodeTooltipProps } from '../Graph/nodes/BaseNode/NodeTooltip/NodeTooltip';
import { ContainerType } from './ThresholdsTab/types';

type RuleEditorContextType = {
  ruleEditorFlatCaretIndices: string[];
  ruleEditorPanelBoxContainerWidth: number;
  ruleItemRefs: RuleItemRefs;
  updateRuleItemRefs: (refItem: RuleItemRef) => void;
  semanticLineBreakData: {
    groupedByLines: SemanticLine[];
    lineBreakPointIndices: string[];
  };
  sourceAction: Action;
  mlaUsedInRules: RulesWithMLA;
  pendingGroupMaxRange: {
    parentOfGroupOpeningPath: string;
    itemsWithinMaxRange: (IndexedRule | BooleanRule)[] | (ExpressionTerm | ExpressionTerm[])[]
    | RuleUpdateErrorType;
    indexOfMaxRangeLastItem: number;
    allPossibleGroupClosingPaths: string[];
  };
  isPreviewAction?: boolean;
  isolatedEditingBlock: IsolatedEditingBlockData;
  containerType?: ContainerType;
  setIsolatedEditingBlock: (_editingBlockState: IsolatedEditingBlockData) => void;
  ActionHoverTooltip?: (props: NodeTooltipProps) => JSX.Element;
} & CaretControlContext;

export const RuleEditorDefaultContext: RuleEditorContextType = {
  ruleEditorFlatCaretIndices: [],
  ruleEditorPanelBoxContainerWidth: 0,
  ruleItemRefs: {},
  updateRuleItemRefs: refItem => {},
  semanticLineBreakData: {
    groupedByLines: [{
      items: [''],
      id: null,
      width: 0,
      mlaEditorInThisLine: false,
    }],
    lineBreakPointIndices: [],
  },
  sourceAction: {} as Action,
  isPreviewAction: false,
  mlaUsedInRules: [],
  morphingCaretPosition: '',
  setMorphingCaretPosition: _position => {},
  getFlattenedCaretPositionIndex: ({
    itemPath,
    mode,
    insertion,
    type,
  }): string => undefined,
  pendingMorphingCaretPositionUpdateValue: undefined,
  setPendingMorphingCaretPositionUpdateValue: _pendingPositionIdx => {},
  activeInputValue: {
    value: '',
    path: '',
    initialValue: false,
  },
  setActiveInputValue: _inputObject => {},
  getPreviousCaretPosition: ({
    userActionObject,
  }: { userActionObject: UserActionObject }): CaretPositionGetterReturnValues => undefined,
  getNextCaretPosition: ({
    userActionObject,
  }: { userActionObject: UserActionObject }): CaretPositionGetterReturnValues => undefined,
  getNewPositionToJump: ({
    userActionObject,
  }: { userActionObject: UserActionObject }): CaretPositionGetterReturnValues => undefined,
  pendingGroupMaxRange: {
    parentOfGroupOpeningPath: '',
    itemsWithinMaxRange: [],
    indexOfMaxRangeLastItem: 0,
    allPossibleGroupClosingPaths: [],
  },
  isolatedEditingBlock: {
    originPublicId: '',
    originType: undefined,
    originCaretIndex: undefined,
  } as IsolatedEditingBlockData,
  containerType: ContainerType.DRAWER,
  setIsolatedEditingBlock: _editingBlockState => {},
  ActionHoverTooltip: null,
};

export const RuleEditorContext = createContext<RuleEditorContextType>(RuleEditorDefaultContext);
